import { EntityBuilder } from '../../../domain/entity/builder'
import { StorageItem } from '../item'
import { Store } from '../keys'

/**
 * Handles local storage, presently cookies and localStorage (use this in place of either of those).
 * All values are timestamped, so most recent takes precedence.
 *
 * NOTE: Use this for per-property storage, and `GlobalStorage` for cross-property.
 */
class RedundantLocalStorage {
    protected cookie = {
        delim: ';',
        keys: {
            expires: 'expires',
            path: 'path',
            samesite: 'SameSite',
            secure: 'Secure'
        },
        path: '/',
        samesite: {
            lax: 'Lax',
            none: 'None',
            strict: 'Strict'
        },
        sep: '=',

        delete(key: Store) {
            document.cookie = [
                ` ${key}${this.sep}`,
                ` ${this.keys.expires}${this.sep}${new Date(0).toUTCString()}`,
                ` ${this.keys.path}${this.sep}${this.path}`,
                ` ${this.keys.secure}`,
                ` ${this.keys.samesite}${this.sep}${this.samesite.none}`].join(this.delim)
        },

        get<T>(key: Store): StorageItem<T> | undefined {
            const raw = JSON.parse(
                (document.cookie.split(this.delim).find((c) =>
                c.trim().startsWith(`${key}${this.sep}`)) || '')
                .trim().split(this.sep).pop() || 'null')

            const item = EntityBuilder.buildOne<StorageItem<T>>(StorageItem, raw)

            // invalid, delete
            if (!item.isValid()) {
                return this.delete(key)
            }

            return item
        },

        set(key: Store, item: string, expireMs: number) {
            document.cookie = [
                ` ${key}${this.sep}${item}`,
                ` ${this.keys.expires}${this.sep}${new Date(Date.now() + expireMs).toUTCString()}`,
                ` ${this.keys.path}${this.sep}${this.path}`,
                ` ${this.keys.secure}`,
                ` ${this.keys.samesite}${this.sep}${this.samesite.none}`].join(this.delim)
        }
    }
    protected local = {
        delete(key: Store) {
            localStorage.removeItem(key)
        },

        get<T>(key: Store): StorageItem<T> | undefined {
            const item = EntityBuilder.buildOne<StorageItem<T>>(
                StorageItem,
                JSON.parse(localStorage.getItem(key) || 'null'))

            // invalid, delete
            if (!item.isValid()) {
                return this.delete(key)
            }

            return item
        },

        set(key: Store, item: string) {
            localStorage.setItem(key, item.toString())
        }
    }

    public delete(key: Store) {
        try {
            this.cookie.delete(key)
            this.local.delete(key)
            return true
        } catch (e) {
            return false
        }
    }

    public get<T>(key: Store): T | undefined {
        const result = [...[].concat(this.cookie.get(key)), ...[].concat(this.local.get(key))]
            .reduce((prev, cur) => cur && prev && cur.data && prev.data && prev.ts > cur.ts || !cur ? prev : cur)
        if (result) {
            return result.data
        }
        return undefined
    }

    public set<T>(key: Store, data: T, expireMs?: number) {
        try {
            const item = new StorageItem<T>(data, expireMs)
            this.cookie.set(key, item.toString(), item.exp)
            this.local.set(key, item.toString())
            return true
        } catch (e) {
            return false
        }
    }
}

export const LocalStore = new RedundantLocalStorage()
